import { cva } from 'class-variance-authority';
import {
  Check,
  CircleAlert,
  Cloud,
  Download,
  LoaderCircle,
  Lock,
  MessageSquareWarning,
  Trash2,
  UploadCloud,
  X,
} from 'lucide-react';
import {
  forwardRef,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Button } from '@/components/atoms/buttons/Button';
import { Checkbox } from '@/components/atoms/forms/Checkbox';
import { Input } from '@/components/atoms/forms/Input';
import {
  type FileExtension,
  FileTypeIcon,
} from '@/components/molecules/files/FileTypeIcon';
import { useDropZone } from '@/hooks/use-drop-zone';
import { cn } from '@/lib/utils';

/* -------------------------------------------------------------------------------------------------
 * FileUploadArea - Drag and drop upload zone
 * -------------------------------------------------------------------------------------------------*/

const fileUploadAreaVariants = cva(
  'relative flex min-h-[168px] w-full flex-col items-center justify-center rounded-lg border border-dashed transition-[border-color,background-color] duration-(--duration-base) ease-standard motion-reduce:transition-none',
  {
    variants: {
      state: {
        default: 'border-border bg-background',
        hover: 'border-info bg-info/10',
        disabled: 'cursor-not-allowed border-border bg-muted opacity-50',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  },
);

export interface FileUploadAreaProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrop'> {
  /** Text for the click action */
  clickText?: string;
  /** Text for drag and drop hint */
  dragText?: string;
  /** Supporting text showing accepted formats */
  supportingText?: string;
  /** Whether the upload area is disabled */
  disabled?: boolean;
  /** Callback when files are dropped or selected */
  onFilesSelected?: (files: FileList) => void;
  /** Accept attribute for file input */
  accept?: string;
  /** Whether multiple files can be selected */
  multiple?: boolean;
}

const FileUploadArea = forwardRef<HTMLDivElement, FileUploadAreaProps>(
  (
    {
      className,
      clickText = 'Browse files',
      dragText = 'Drag and drop your file',
      supportingText = 'or click to browse from your computer',
      disabled = false,
      onFilesSelected,
      accept,
      multiple = true,
      ...props
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

    const { isDragging, dropZoneProps, openFilePicker } = useDropZone({
      onFilesSelected,
      accept,
      multiple,
      disabled,
      containerRef,
    });

    const state = disabled ? 'disabled' : isDragging ? 'hover' : 'default';

    return (
      <div
        ref={containerRef}
        className={cn(
          fileUploadAreaVariants({ state }),
          'group/upload px-6 py-5',
          className,
        )}
        {...dropZoneProps}
        data-slot="file-upload-area"
        data-dragging={isDragging ? '' : undefined}
        aria-disabled={disabled}
        {...props}
      >
        <div className="flex w-full flex-col items-center gap-4">
          <div
            className={cn(
              'flex size-10 items-center justify-center rounded-full border bg-secondary text-secondary-foreground backdrop-blur-[2px]',
              isDragging ? 'border-info text-info' : 'border-border',
            )}
          >
            <UploadCloud className="size-4" aria-hidden="true" />
          </div>

          <div className="flex w-full flex-col items-center gap-1.5">
            <p className="text-sm font-semibold text-secondary-foreground">
              {dragText}
            </p>
            <p className="text-center text-xs text-secondary-foreground">
              {supportingText}
            </p>
            <Button
              type="button"
              size="sm"
              appearance="solid"
              variant="primary"
              disabled={disabled}
              onClick={openFilePicker}
              aria-label={clickText}
              className="mt-0 h-[30px] rounded-xl px-3"
            >
              {clickText}
            </Button>
          </div>
        </div>
      </div>
    );
  },
);

FileUploadArea.displayName = 'FileUploadArea';

/* -------------------------------------------------------------------------------------------------
 * FileUploadItem - Individual file item with upload progress
 * -------------------------------------------------------------------------------------------------*/

export type FileUploadStatus =
  | 'uploading'
  | 'complete'
  | 'error'
  | 'disabled'
  | 'cloud';

const fileUploadItemVariants = cva(
  'relative overflow-hidden rounded-2xl border transition-colors duration-(--duration-fast) ease-standard motion-reduce:transition-none',
  {
    variants: {
      status: {
        uploading: 'border-border bg-card',
        complete: 'border-border bg-card',
        cloud: 'border-border bg-card',
        disabled: 'border-border bg-muted',
        error: 'border-destructive bg-card',
      },
      mobile: {
        true: 'size-[100px] min-h-0 max-w-none',
        false: 'min-h-[72px] w-full max-w-[512px]',
      },
    },
    defaultVariants: {
      status: 'uploading',
      mobile: false,
    },
  },
);

export interface FileUploadItemProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** File name to display */
  fileName: string;
  /** File size string (e.g., "200 KB", "16 MB") */
  fileSize?: string;
  /** File extension for icon display */
  fileExtension?: FileExtension | string;
  /** Upload progress (0-100) */
  progress?: number;
  /** Whether metadata should include the derived upload percentage */
  showProgress?: boolean;
  /** Upload status */
  status?: FileUploadStatus;
  /** Render the 100x100 mobile mini-card from the Figma component set */
  mobile?: boolean;
  /** Custom element to render next to the title (e.g., badge, tag, icon) */
  titleAddon?: React.ReactNode;
  /** Called when delete/cancel is clicked */
  onRemove?: () => void;
  /** Called when cancel is clicked (during upload) */
  onCancel?: () => void;
  /** Called when retry is clicked (on error) */
  onRetry?: () => void;
  /** Custom actions to render (replaces default RemoveButton when provided) */
  actions?: React.ReactNode;
}

const actionBtnBase =
  'inline-flex items-center justify-center rounded-full text-muted-foreground transition-[color,background-color,transform] duration-150 ease-smooth hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring active:scale-90 motion-reduce:transition-none motion-reduce:active:scale-100';

// A consumer status update may replace Retry/Cancel while it owns focus.
// Repair only a disconnected focused action, never a consumer's external focus.
function useUploadActionFocus(ref: React.RefObject<HTMLDivElement | null>) {
  const focused = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const root = ref.current;
    const doc = root?.ownerDocument;
    if (
      root &&
      doc &&
      focused.current &&
      !focused.current.isConnected &&
      doc.activeElement === doc.body
    ) {
      root.querySelector<HTMLElement>('button:not(:disabled)')?.focus();
    }
  });
  return (event: React.FocusEvent<HTMLDivElement>) => {
    focused.current = event.target as HTMLElement;
  };
}

function normalizeUploadProgress(progress: number): number {
  if (!Number.isFinite(progress)) {
    return 0;
  }

  return Math.min(100, Math.max(0, progress));
}

function UploadSpinner({
  size = 32,
  className,
}: {
  size?: number;
  className?: string;
}) {
  const strokeWidth = size <= 20 ? 2.5 : 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * 0.25;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      aria-hidden="true"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--border)"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--primary)"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={`${arcLength} ${circumference - arcLength}`}
        strokeDashoffset={circumference * 0.25}
      />
    </svg>
  );
}

function getUploadMeta({
  fileSize,
  progress,
  status,
  showProgress = true,
}: {
  fileSize?: string;
  progress: number;
  status: FileUploadStatus;
  showProgress?: boolean;
}): string {
  if (status === 'error') {
    return 'Upload failed, please try again';
  }

  if (!showProgress) {
    return fileSize ?? '';
  }

  const uploadedProgress =
    status === 'complete' || status === 'cloud' ? 100 : progress;
  const sizePrefix = fileSize ? `${fileSize} \u2013 ` : '';
  return `${sizePrefix}${uploadedProgress}% uploaded`;
}

// Increases may trail the real value; corrections are immediate. Never animate
// backwards or leave the old target visible above the latest consumer value.
function UploadProgress({
  progress,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { progress: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const lastWidth = useRef<number | undefined>(undefined);
  const previousProgress = useRef(progress);
  const [initialProgress] = useState(progress);
  useLayoutEffect(() => {
    const fill = ref.current;
    const view = fill?.ownerDocument.defaultView;
    if (!fill || !view) return;
    const media = view.matchMedia('(prefers-reduced-motion: reduce)');
    const increased = progress > previousProgress.current;
    previousProgress.current = progress;
    const from = lastWidth.current ?? progress;
    for (const animation of fill.getAnimations()) animation.cancel();
    fill.style.width = `${progress}%`;
    const styles = view.getComputedStyle(fill);
    const animation =
      !media.matches && increased && from < progress - 0.01
        ? fill.animate([{ width: `${from}%` }, { width: `${progress}%` }], {
            duration:
              Number.parseFloat(
                styles.getPropertyValue('--motion-duration-moderate'),
              ) || 200,
            easing:
              styles.getPropertyValue('--motion-easing-standard').trim() ||
              'ease-out',
          })
        : undefined;
    const settle = () => animation?.cancel();
    media.addEventListener('change', settle);
    return () => {
      const width = fill.parentElement?.clientWidth ?? 0;
      lastWidth.current = width
        ? (fill.getBoundingClientRect().width / width) * 100
        : progress;
      settle();
      media.removeEventListener('change', settle);
    };
  }, [progress]);
  return (
    <div
      ref={ref}
      data-slot="file-upload-progress-fill"
      className={className}
      style={{ width: `${initialProgress}%` }}
      {...props}
    />
  );
}

function ProgressFill({
  fileName,
  progress,
}: {
  fileName: string;
  progress: number;
}) {
  return (
    <UploadProgress
      progress={progress}
      className="absolute inset-y-0 start-0 bg-accent"
      role="progressbar"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Uploading ${fileName}: ${progress}%`}
    />
  );
}

function useUploadStatusFade(
  ref: React.RefObject<HTMLElement | null>,
  status: FileUploadStatus,
) {
  const previous = useRef(status);
  useLayoutEffect(() => {
    const changed = previous.current !== status;
    previous.current = status;
    const icon = ref.current;
    const view = icon?.ownerDocument.defaultView;
    if (!changed || !icon || !view) return;
    const media = view.matchMedia('(prefers-reduced-motion: reduce)');
    if (media.matches) return;
    const styles = view.getComputedStyle(icon);
    const animation = icon.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration:
        Number.parseFloat(styles.getPropertyValue('--motion-duration-fast')) ||
        120,
      easing:
        styles.getPropertyValue('--motion-easing-standard').trim() ||
        'ease-out',
    });
    const settle = () => animation.cancel();
    media.addEventListener('change', settle);
    return () => {
      settle();
      media.removeEventListener('change', settle);
    };
  }, [ref, status]);
}

function StatusBadge({
  status,
  mobile = false,
}: {
  status: FileUploadStatus;
  mobile?: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  useUploadStatusFade(ref, status);
  if (status !== 'cloud' && status !== 'disabled' && status !== 'complete') {
    return null;
  }

  const Icon =
    status === 'complete' ? Check : status === 'cloud' ? Cloud : Lock;
  const label =
    status === 'complete'
      ? 'Upload complete'
      : status === 'cloud'
        ? 'Cloud file'
        : 'File locked';

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center',
        mobile ? 'size-4' : 'size-7 rounded-xl',
        status === 'cloud' &&
          (mobile ? 'text-primary' : 'bg-primary/10 text-primary'),
        status === 'disabled' &&
          (mobile ? 'text-destructive' : 'bg-destructive/10 text-destructive'),
        status === 'complete' && 'text-success',
      )}
      ref={ref}
      data-slot="file-upload-status"
      aria-label={label}
      role="img"
    >
      <Icon className="size-4" aria-hidden="true" />
    </span>
  );
}

function DesktopAction({
  fileName,
  status,
  onRemove,
  onCancel,
  actions,
}: {
  fileName: string;
  status: FileUploadStatus;
  onRemove?: () => void;
  onCancel?: () => void;
  actions?: React.ReactNode;
}) {
  const isUploading = status === 'uploading';
  const isTerminal =
    status === 'complete' ||
    status === 'cloud' ||
    status === 'disabled' ||
    status === 'error';

  if (actions) {
    return (
      <div
        className="relative z-10 flex shrink-0 flex-wrap items-center gap-1"
        data-slot="actions"
      >
        {actions}
      </div>
    );
  }

  return (
    <>
      {isUploading && (
        <div className="absolute end-3 top-3 z-10 flex flex-col items-center gap-1">
          <UploadSpinner size={32} />
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              aria-label={`Cancel upload of ${fileName}`}
              className="text-xs font-medium text-primary transition-colors duration-150 ease-smooth hover:text-primary/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring motion-reduce:transition-none"
            >
              Cancel
            </button>
          )}
        </div>
      )}
      {isTerminal && onRemove && (
        <div className="absolute end-2 top-2 z-10">
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${fileName}`}
            disabled={status === 'disabled'}
            className={cn(
              'size-7',
              actionBtnBase,
              status === 'disabled' && 'pointer-events-none opacity-50',
            )}
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      )}
      <div className="absolute bottom-2 end-2 z-10">
        <StatusBadge status={status} />
      </div>
    </>
  );
}

function DesktopFileItem({
  fileName,
  fileSize,
  fileExtension,
  progress,
  showProgress,
  status,
  onRemove,
  onCancel,
  onRetry,
  titleAddon,
  actions,
}: {
  fileName: string;
  fileSize?: string;
  fileExtension?: FileExtension | string;
  progress: number;
  showProgress: boolean;
  status: FileUploadStatus;
  onRemove?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  titleAddon?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  const isUploading = status === 'uploading';
  const isError = status === 'error';
  const isDisabled = status === 'disabled';
  const meta = getUploadMeta({ fileSize, progress, status, showProgress });

  return (
    <>
      {isUploading && <ProgressFill fileName={fileName} progress={progress} />}

      <div
        className={cn(
          'relative flex items-start gap-3 px-4 py-4',
          isError ? 'min-h-24' : 'min-h-[72px]',
          !actions && (isError ? 'pe-12' : isUploading ? 'pe-20' : 'pe-14'),
        )}
      >
        <FileTypeIcon
          extension={fileExtension}
          size="md"
          variant="default"
          className={cn('shrink-0', isDisabled && 'opacity-50')}
        />

        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col items-start',
            isError ? 'gap-2' : 'gap-0',
          )}
        >
          <div
            data-slot="file-upload-title-row"
            className="flex w-full min-w-0 items-center gap-2"
          >
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-sm font-medium',
                isDisabled ? 'text-muted-foreground' : 'text-card-foreground',
              )}
            >
              {fileName}
            </span>
            {titleAddon && <span className="shrink-0">{titleAddon}</span>}
          </div>
          <span
            className={cn(
              'max-w-full text-sm',
              isDisabled
                ? 'text-muted-foreground'
                : 'text-secondary-foreground',
            )}
            aria-live={isUploading ? 'polite' : undefined}
          >
            {meta}
          </span>
          {isError && onRetry && (
            <button
              type="button"
              onClick={onRetry}
              aria-label={`Retry upload of ${fileName}`}
              className="text-xs font-medium text-primary transition-colors duration-150 ease-smooth hover:text-primary/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring motion-reduce:transition-none"
            >
              Retry
            </button>
          )}
        </div>

        <DesktopAction
          fileName={fileName}
          status={status}
          onRemove={onRemove}
          onCancel={onCancel}
          actions={actions}
        />
      </div>
    </>
  );
}

function MobileFileItem({
  fileName,
  fileExtension,
  progress,
  status,
  onRemove,
  onCancel,
  onRetry,
  actions,
}: {
  fileName: string;
  fileExtension?: FileExtension | string;
  progress: number;
  status: FileUploadStatus;
  onRemove?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  actions?: React.ReactNode;
}) {
  const isUploading = status === 'uploading';
  const isError = status === 'error';
  const isDisabled = status === 'disabled';
  const isTerminal =
    status === 'complete' ||
    status === 'cloud' ||
    status === 'disabled' ||
    status === 'error';

  return (
    <>
      {isUploading && <ProgressFill fileName={fileName} progress={progress} />}
      <div className="relative flex size-full flex-col justify-between p-2.5">
        <div className="flex items-start justify-between">
          <FileTypeIcon
            extension={fileExtension}
            size="xs"
            variant="default"
            className={cn('shrink-0', isDisabled && 'opacity-50')}
          />
          <span className="absolute start-1/2 top-2 -translate-x-1/2">
            <StatusBadge status={status} mobile />
          </span>
          <div className="absolute end-2 top-2">
            {actions ? (
              <span data-slot="actions">{actions}</span>
            ) : (
              <>
                {isUploading &&
                  (onCancel ? (
                    <button
                      type="button"
                      onClick={onCancel}
                      aria-label={`Cancel upload of ${fileName}`}
                      className="inline-flex size-4 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    >
                      <UploadSpinner size={16} />
                    </button>
                  ) : (
                    <UploadSpinner size={16} />
                  ))}
                {isTerminal && onRemove && (
                  <button
                    type="button"
                    onClick={onRemove}
                    aria-label={`Remove ${fileName}`}
                    disabled={isDisabled}
                    className={cn(
                      'size-4',
                      actionBtnBase,
                      isDisabled && 'pointer-events-none opacity-50',
                    )}
                  >
                    <Trash2 className="size-3" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="relative flex flex-col items-start gap-1">
          <p
            className={cn(
              'max-w-full overflow-hidden text-ellipsis break-words text-sm leading-5 [display:-webkit-box] [-webkit-box-orient:vertical]',
              isError
                ? '[-webkit-line-clamp:2]'
                : 'h-[60px] [-webkit-line-clamp:3]',
              isDisabled ? 'text-muted-foreground' : 'text-card-foreground',
            )}
          >
            {isError ? 'Upload failed.' : fileName}
          </p>
          {isError && onRetry && (
            <button
              type="button"
              onClick={onRetry}
              aria-label={`Retry upload of ${fileName}`}
              className="text-xs font-medium text-primary transition-colors duration-150 ease-smooth hover:text-primary/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring motion-reduce:transition-none"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </>
  );
}

const FileUploadItem = forwardRef<HTMLDivElement, FileUploadItemProps>(
  (
    {
      className,
      fileName,
      fileSize,
      fileExtension,
      progress: rawProgress = 0,
      showProgress = true,
      status = 'uploading',
      mobile = false,
      titleAddon,
      onRemove,
      onCancel,
      onRetry,
      actions,
      ...props
    },
    ref,
  ) => {
    const progress = normalizeUploadProgress(rawProgress);
    const rootRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => rootRef.current as HTMLDivElement);
    const repairFocus = useUploadActionFocus(rootRef);

    return (
      <div
        ref={rootRef}
        data-slot="file-upload-item"
        data-status={status}
        data-mobile={mobile ? '' : undefined}
        className={cn(fileUploadItemVariants({ status, mobile }), className)}
        {...props}
        onFocusCapture={(event) => {
          repairFocus(event);
          props.onFocusCapture?.(event);
        }}
      >
        {mobile ? (
          <MobileFileItem
            fileName={fileName}
            fileExtension={fileExtension}
            progress={progress}
            status={status}
            onRemove={onRemove}
            onCancel={onCancel}
            onRetry={onRetry}
            actions={actions}
          />
        ) : (
          <DesktopFileItem
            fileName={fileName}
            fileSize={fileSize}
            fileExtension={fileExtension}
            progress={progress}
            showProgress={showProgress}
            status={status}
            onRemove={onRemove}
            onCancel={onCancel}
            onRetry={onRetry}
            titleAddon={titleAddon}
            actions={actions}
          />
        )}
      </div>
    );
  },
);

FileUploadItem.displayName = 'FileUploadItem';

/* -------------------------------------------------------------------------------------------------
 * FileUpload - Combined component with area and queue
 * -------------------------------------------------------------------------------------------------*/

export interface UploadFile {
  id: string;
  name: string;
  size: string;
  extension?: FileExtension;
  progress: number;
  showProgress?: boolean;
  status: FileUploadStatus;
}

export type FileUploadView = 'default' | 'stack' | 'list';

export interface FileUploadProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Files in the upload queue */
  files?: UploadFile[];
  /** Props for the upload area */
  areaProps?: Omit<FileUploadAreaProps, 'onFilesSelected'>;
  /** Help text displayed below the upload target */
  helpText?: React.ReactNode;
  /** Called when files are selected */
  onFilesSelected?: (files: FileList) => void;
  /** Called when a file is removed */
  onFileRemove?: (fileId: string) => void;
  /** Called when a file upload is cancelled */
  onFileCancel?: (fileId: string) => void;
  /** Called when a failed file upload is retried */
  onFileRetry?: (fileId: string) => void;
  /** Uploaded-file presentation from the Figma component set */
  fileView?: FileUploadView;
  /** Show the optional upload-from-URL control */
  uploadFromUrl?: boolean;
  /** Controlled URL value */
  uploadUrl?: string;
  /** Initial URL value for uncontrolled usage */
  defaultUploadUrl?: string;
  /** Placeholder shown in the URL input */
  uploadUrlPlaceholder?: string;
  /** Called when the URL input changes */
  onUploadUrlChange?: (url: string) => void;
  /** Called when the URL upload action is submitted */
  onUrlUpload?: (url: string) => void;
  /** Controlled selected file IDs for list view */
  selectedFileIds?: string[];
  /** Initially selected file IDs for uncontrolled list view */
  defaultSelectedFileIds?: string[];
  /** Called when list-view selection changes */
  onSelectedFileIdsChange?: (fileIds: string[]) => void;
  /** Called when selected files are downloaded from list view */
  onFilesDownload?: (fileIds: string[]) => void;
}

type FileUploadShellState =
  | 'default'
  | 'uploading'
  | 'error'
  | 'complete'
  | 'many';

function getFileUploadShellState(files: UploadFile[]): FileUploadShellState {
  if (files.length === 0) {
    return 'default';
  }

  if (files.length === 1) {
    const status = files[0]?.status;
    if (status === 'uploading' || status === 'error' || status === 'complete') {
      return status;
    }
  }

  return 'many';
}

function FileUploadSupportText({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex w-full items-center justify-center gap-1 text-center text-xs text-secondary-foreground"
      data-slot="file-upload-support"
    >
      <CircleAlert className="size-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

function UploadStateTarget({
  file,
  onBrowse,
  onCancel,
  onRemove,
  onRetry,
}: {
  file: UploadFile;
  onBrowse: () => void;
  onCancel?: () => void;
  onRemove?: () => void;
  onRetry?: () => void;
}) {
  const status = file.status as 'uploading' | 'error' | 'complete';
  const rootRef = useRef<HTMLDivElement>(null);
  const repairFocus = useUploadActionFocus(rootRef);
  const iconRef = useRef<HTMLDivElement>(null);
  useUploadStatusFade(iconRef, status);
  const progress =
    status === 'complete' ? 100 : normalizeUploadProgress(file.progress);
  const isUploading = status === 'uploading';
  const isError = status === 'error';
  const statusText = isUploading
    ? 'Uploading'
    : isError
      ? 'Upload failed, please try again'
      : 'Uploaded';
  const fileType =
    file.extension === 'pdf' ? 'doc/pdf' : file.extension?.toLowerCase();
  const meta = isUploading
    ? `${statusText} - ${file.size}`
    : isError
      ? statusText
      : `${statusText} - ${file.size}${fileType ? ` ${fileType}` : ''}`;
  const Icon = isUploading
    ? LoaderCircle
    : isError
      ? MessageSquareWarning
      : Check;

  return (
    <div
      className={cn(
        'flex min-h-[168px] w-full flex-col items-center justify-center rounded-lg border px-6 py-5',
        isUploading && 'border-dashed border-border bg-muted',
        isError && 'border-destructive bg-destructive/10',
        status === 'complete' && 'border-success bg-success/10',
      )}
      ref={rootRef}
      onFocusCapture={repairFocus}
      data-slot="file-upload-state-target"
    >
      <div className="flex w-full flex-col items-center gap-4">
        <div
          ref={iconRef}
          data-slot="file-upload-status"
          className={cn(
            'flex size-10 items-center justify-center rounded-full border bg-secondary',
            isUploading && 'border-border text-secondary-foreground',
            isError && 'border-warning text-warning',
            status === 'complete' && 'border-success text-success',
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
        </div>

        <div className="flex w-full flex-col items-center gap-3">
          <div className="flex max-w-full flex-col items-center gap-3">
            <p className="max-w-full truncate text-sm font-medium text-card-foreground">
              {file.name}
            </p>
            <p className="text-xs text-secondary-foreground">{meta}</p>
          </div>

          <div
            className="h-1.5 w-[200px] max-w-full overflow-hidden rounded-full bg-primary/20"
            role="progressbar"
            aria-label={`${file.name}: ${progress}%`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          >
            <UploadProgress
              progress={progress}
              className={cn(
                'h-full rounded-full',
                isError ? 'bg-destructive' : 'bg-primary',
              )}
            />
          </div>

          <div className="flex items-center justify-center gap-3">
            {isUploading && onCancel && (
              <Button
                type="button"
                size="sm"
                appearance="solid"
                variant="secondary"
                startIcon={<X className="size-4" />}
                onClick={onCancel}
                aria-label={`Cancel upload of ${file.name}`}
                className="h-[30px] rounded-xl px-3"
              >
                Cancel
              </Button>
            )}
            {isError && onRetry && (
              <Button
                type="button"
                size="sm"
                appearance="solid"
                variant="secondary"
                onClick={onRetry}
                aria-label={`Try again: upload ${file.name}`}
                className="h-[30px] rounded-xl px-3"
              >
                Try again
              </Button>
            )}
            {status === 'complete' && (
              <Button
                type="button"
                size="sm"
                appearance="solid"
                variant="primary"
                onClick={onBrowse}
                className="h-[30px] rounded-xl px-3"
              >
                Upload another
              </Button>
            )}
            {(isError || status === 'complete') && onRemove && (
              <Button
                type="button"
                size="sm"
                appearance="solid"
                variant="secondary"
                onClick={onRemove}
                aria-label={`Delete ${file.name}`}
                className="h-[30px] rounded-xl px-3"
              >
                Delete
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function UploadFromUrl({
  value,
  placeholder,
  onChange,
  onUpload,
}: {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  onUpload?: (value: string) => void;
}) {
  const inputId = useId();

  return (
    <form
      className="flex w-full flex-col items-center gap-2.5 py-2.5"
      data-slot="upload-from-url"
      onSubmit={(event) => {
        event.preventDefault();
        const trimmedValue = value.trim();
        if (trimmedValue) {
          onUpload?.(trimmedValue);
        }
      }}
    >
      <div className="flex w-full items-center gap-2.5">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs text-secondary-foreground">or</span>
        <span className="h-px flex-1 bg-border" />
      </div>
      <label htmlFor={inputId} className="text-xs text-secondary-foreground">
        Upload from URL
      </label>
      <div className="flex w-[360px] max-w-full">
        <Input
          id={inputId}
          type="url"
          size="md"
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.currentTarget.value)}
          aria-label="Upload from URL"
          className="rounded-e-none"
        />
        <Button
          type="submit"
          size="default"
          appearance="solid"
          variant="primary"
          aria-label="Upload URL"
          className="rounded-s-none rounded-e-xl px-4"
        >
          Upload
        </Button>
      </div>
    </form>
  );
}

function UploadRow({
  children,
  arriving,
  exiting,
  compact,
  onExited,
  id,
}: {
  children: React.ReactNode;
  arriving: boolean;
  exiting: boolean;
  compact: boolean;
  onExited: () => void;
  id: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const exitRef = useRef(onExited);
  const interrupted = useRef<
    { opacity: number; height: string; paddingBottom: string } | undefined
  >(undefined);
  const arrived = useRef(!arriving);
  exitRef.current = onExited;
  useLayoutEffect(() => {
    const row = ref.current;
    const view = row?.ownerDocument.defaultView;
    if (!row || !view || (arrived.current && !exiting && !interrupted.current))
      return;
    const media = view.matchMedia('(prefers-reduced-motion: reduce)');
    if (media.matches) {
      arrived.current = true;
      if (exiting) exitRef.current();
      return;
    }
    const styles = view.getComputedStyle(row);
    const height = row.getBoundingClientRect().height;
    const full = {
      opacity: 1,
      height: `${height}px`,
      paddingBottom: styles.paddingBottom,
    };
    const empty = {
      opacity: 0,
      height: compact ? `${height}px` : '0px',
      paddingBottom: compact ? styles.paddingBottom : '0px',
    };
    const from = interrupted.current ?? (exiting ? full : empty);
    interrupted.current = undefined;
    const animation = row.animate([from, exiting ? empty : full], {
      duration:
        Number.parseFloat(styles.getPropertyValue('--motion-duration-base')) ||
        160,
      easing:
        styles.getPropertyValue('--motion-easing-standard').trim() ||
        'ease-out',
      fill: 'both',
    });
    const finish = () => {
      arrived.current = true;
      animation.cancel();
      if (exiting) exitRef.current();
    };
    animation.onfinish = finish;
    media.addEventListener('change', finish);
    return () => {
      const current = view.getComputedStyle(row);
      interrupted.current = {
        opacity: Number(current.opacity),
        height: current.height,
        paddingBottom: current.paddingBottom,
      };
      animation.onfinish = null;
      animation.cancel();
      media.removeEventListener('change', finish);
    };
  }, [exiting, compact]);
  return (
    <div
      ref={ref}
      data-slot="file-upload-row"
      data-file-id={id}
      data-exiting={exiting ? '' : undefined}
      inert={exiting || undefined}
      aria-hidden={exiting || undefined}
      className={cn(
        'overflow-hidden pb-1.5 [&>div]:mx-auto',
        compact ? 'w-[100px]' : 'w-full',
      )}
    >
      {children}
    </div>
  );
}

function UploadedFiles({
  files,
  view,
  selectedFileIds,
  onSelectedFileIdsChange,
  onFileRemove,
  onFileCancel,
  onFileRetry,
  onFilesDownload,
  seenFileIds,
}: {
  seenFileIds: ReadonlySet<string>;
  files: UploadFile[];
  view: FileUploadView;
  selectedFileIds: string[];
  onSelectedFileIdsChange: (fileIds: string[]) => void;
  onFileRemove?: (fileId: string) => void;
  onFileCancel?: (fileId: string) => void;
  onFileRetry?: (fileId: string) => void;
  onFilesDownload?: (fileIds: string[]) => void;
}) {
  const rootRef = useRef<HTMLFieldSetElement>(null);
  const [previousFiles, setPreviousFiles] = useState(files);
  const [rows, setRows] = useState(() =>
    files.map((file) => ({ file, arriving: !seenFileIds.has(file.id) })),
  );
  if (previousFiles !== files) {
    setPreviousFiles(files);
    const previous = new Map(rows.map((row) => [row.file.id, row]));
    const ids = new Set(files.map((file) => file.id));
    const nextRows = files.map((file) => ({
      file,
      arriving: previous.get(file.id)?.arriving ?? !seenFileIds.has(file.id),
    }));
    rows.forEach((row, index) => {
      if (!ids.has(row.file.id)) nextRows.splice(index, 0, row);
    });
    setRows(nextRows);
  }
  useLayoutEffect(() => {
    const root = rootRef.current;
    const active = root?.ownerDocument.activeElement;
    if (!root || !active?.closest('[data-exiting]')) return;
    const removed = active.closest<HTMLElement>('[data-file-id]');
    if (!removed) return;
    const allRows = [...root.querySelectorAll<HTMLElement>('[data-file-id]')];
    const index = allRows.indexOf(removed);
    const candidates = [
      ...allRows.slice(index + 1),
      ...allRows.slice(0, index).reverse(),
    ];
    const next = candidates
      .filter((row) => !row.hasAttribute('data-exiting'))
      .map((row) =>
        row.querySelector<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), [role="checkbox"]',
        ),
      )
      .find(Boolean);
    (
      next ??
      root
        .closest('[data-slot="file-upload"]')
        ?.querySelector<HTMLElement>('button:not(:disabled)')
    )?.focus();
  });
  const toggleFile = (fileId: string, checked: boolean) => {
    const nextSelection = checked
      ? [...selectedFileIds, fileId]
      : selectedFileIds.filter((id) => id !== fileId);
    onSelectedFileIdsChange(nextSelection);
  };

  if (view === 'list') {
    return (
      <fieldset
        ref={rootRef}
        className="m-0 flex min-w-0 w-full flex-col border-0 p-0 py-2.5"
        data-slot="uploaded-files"
        data-view={view}
      >
        <legend className="sr-only">Uploaded files</legend>
        {rows.map(({ file, arriving }) => (
          <UploadRow
            key={file.id}
            id={file.id}
            arriving={arriving}
            exiting={!files.some((current) => current.id === file.id)}
            compact={false}
            onExited={() =>
              setRows((current) =>
                current.filter((row) => row.file.id !== file.id),
              )
            }
          >
            <div className="flex w-full items-center gap-2 rounded-md px-2 py-1.5">
              <Checkbox
                checked={selectedFileIds.includes(file.id)}
                onCheckedChange={(checked) => toggleFile(file.id, checked)}
                aria-label={`Select ${file.name}`}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-card-foreground">
                  {file.name}
                </p>
                <p className="truncate text-xs text-secondary-foreground">
                  {getUploadMeta({
                    fileSize: file.size,
                    progress: normalizeUploadProgress(file.progress),
                    status: file.status,
                    showProgress: file.showProgress,
                  })}
                </p>
              </div>
            </div>
          </UploadRow>
        ))}

        <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
          <Button
            type="button"
            appearance="solid"
            variant="primary"
            size="default"
            startIcon={<Download className="size-4" />}
            disabled={selectedFileIds.length === 0}
            onClick={() => onFilesDownload?.(selectedFileIds)}
            aria-label="Download selected"
            className="rounded-xl px-4"
          >
            Download
          </Button>
          <Button
            type="button"
            appearance="solid"
            variant="secondary"
            size="default"
            startIcon={<Trash2 className="size-4" />}
            disabled={selectedFileIds.length === 0}
            onClick={() => {
              for (const fileId of selectedFileIds) {
                onFileRemove?.(fileId);
              }
            }}
            aria-label="Delete selected"
            className="rounded-xl px-4"
          >
            Delete
          </Button>
        </div>
      </fieldset>
    );
  }

  return (
    <fieldset
      ref={rootRef}
      className={cn(
        'm-0 min-w-0 w-full border-0 p-0 py-2.5',
        view === 'stack'
          ? 'flex flex-wrap items-start justify-center gap-x-1.5'
          : 'flex flex-col items-center',
      )}
      data-slot="uploaded-files"
      data-view={view}
    >
      <legend className="sr-only">Uploaded files</legend>
      {rows.map(({ file, arriving }) => (
        <UploadRow
          key={file.id}
          id={file.id}
          arriving={arriving}
          exiting={!files.some((current) => current.id === file.id)}
          compact={view === 'stack'}
          onExited={() =>
            setRows((current) =>
              current.filter((row) => row.file.id !== file.id),
            )
          }
        >
          <FileUploadItem
            mobile={view === 'stack'}
            fileName={file.name}
            fileSize={file.size}
            fileExtension={file.extension}
            progress={file.progress}
            showProgress={file.showProgress}
            status={file.status}
            onRemove={onFileRemove ? () => onFileRemove(file.id) : undefined}
            onCancel={onFileCancel ? () => onFileCancel(file.id) : undefined}
            onRetry={onFileRetry ? () => onFileRetry(file.id) : undefined}
          />
        </UploadRow>
      ))}
    </fieldset>
  );
}

const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      className,
      files = [],
      areaProps,
      helpText = 'Maximum file size: 50 MB. Supported formats: PNG, JPG, PDF, ZIP.',
      onFilesSelected,
      onFileRemove,
      onFileCancel,
      onFileRetry,
      fileView = 'default',
      uploadFromUrl = false,
      uploadUrl,
      defaultUploadUrl = '',
      uploadUrlPlaceholder = 'https://somewebserver.com/folder/file',
      onUploadUrlChange,
      onUrlUpload,
      selectedFileIds,
      defaultSelectedFileIds = [],
      onSelectedFileIdsChange,
      onFilesDownload,
      ...props
    },
    ref,
  ) => {
    const [internalUploadUrl, setInternalUploadUrl] =
      useState(defaultUploadUrl);
    const [internalSelectedFileIds, setInternalSelectedFileIds] = useState(
      defaultSelectedFileIds,
    );
    const replacementInputRef = useRef<HTMLInputElement>(null);
    const seenFileIds = useRef(new Set(files.map((file) => file.id)));
    useLayoutEffect(() => {
      for (const file of files) seenFileIds.current.add(file.id);
      if (selectedFileIds === undefined) {
        setInternalSelectedFileIds((current) => {
          const next = current.filter((id) =>
            files.some((file) => file.id === id),
          );
          return next.length === current.length ? current : next;
        });
      }
    }, [files, selectedFileIds]);
    const rootRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => rootRef.current as HTMLDivElement);
    const repairFocus = useUploadActionFocus(rootRef);
    const resolvedUploadUrl = uploadUrl ?? internalUploadUrl;
    const resolvedSelectedFileIds = (
      selectedFileIds ?? internalSelectedFileIds
    ).filter((id) => files.some((file) => file.id === id));
    // Once a queue is shown, keep its keyed rows through many→one→empty.
    // Initial single-file uploads retain the existing state-target presentation.
    const needsQueue = getFileUploadShellState(files) === 'many';
    const [queueShown, setQueueShown] = useState(needsQueue);
    if (needsQueue && !queueShown) setQueueShown(true);
    const state =
      queueShown && files.length > 0 ? 'many' : getFileUploadShellState(files);
    const activeFile =
      state === 'uploading' || state === 'error' || state === 'complete'
        ? files[0]
        : undefined;
    const updateUploadUrl = (nextUrl: string) => {
      if (uploadUrl === undefined) {
        setInternalUploadUrl(nextUrl);
      }
      onUploadUrlChange?.(nextUrl);
    };

    const updateSelectedFileIds = (nextFileIds: string[]) => {
      if (selectedFileIds === undefined) {
        setInternalSelectedFileIds(nextFileIds);
      }
      onSelectedFileIdsChange?.(nextFileIds);
    };

    return (
      // biome-ignore lint/a11y/useSemanticElements: FileUpload preserves its public HTMLDivElement ref contract.
      <div
        ref={rootRef}
        className={cn(
          'flex w-full max-w-[600px] flex-col items-center justify-center rounded-xl border border-border bg-card px-6 py-4',
          className,
        )}
        data-slot="file-upload"
        data-state={state}
        role="group"
        aria-label="File upload"
        {...props}
        onFocusCapture={(event) => {
          repairFocus(event);
          props.onFocusCapture?.(event);
        }}
      >
        <div className="flex w-full flex-col items-center gap-3">
          <div className="flex w-full items-center gap-3">
            <span className="min-w-0 flex-1 text-xs font-semibold text-secondary-foreground">
              Upload file
            </span>
            {state === 'uploading' && activeFile && (
              <span className="px-2 py-1 text-[10px] leading-[14px] text-muted-foreground">
                {normalizeUploadProgress(activeFile.progress)}%
              </span>
            )}
            {state === 'error' && activeFile && (
              <span className="px-2 py-1 text-[10px] leading-[14px] text-warning">
                {normalizeUploadProgress(activeFile.progress)}% Failed
              </span>
            )}
            {state === 'complete' && (
              <span className="px-2 py-1 text-[10px] leading-[14px] text-success">
                100% Complete
              </span>
            )}
          </div>

          {activeFile ? (
            <UploadStateTarget
              file={activeFile}
              onBrowse={() => replacementInputRef.current?.click()}
              onCancel={
                onFileCancel ? () => onFileCancel(activeFile.id) : undefined
              }
              onRemove={
                onFileRemove ? () => onFileRemove(activeFile.id) : undefined
              }
              onRetry={
                onFileRetry ? () => onFileRetry(activeFile.id) : undefined
              }
            />
          ) : (
            <FileUploadArea
              onFilesSelected={onFilesSelected}
              {...areaProps}
              dragText={
                areaProps?.dragText ??
                (state === 'many'
                  ? 'Drag and drop your files'
                  : 'Drag and drop your file')
              }
            />
          )}

          <input
            ref={replacementInputRef}
            type="file"
            accept={areaProps?.accept}
            multiple={areaProps?.multiple ?? true}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(event) => {
              if (event.currentTarget.files?.length) {
                onFilesSelected?.(event.currentTarget.files);
              }
              event.currentTarget.value = '';
            }}
          />

          {(state === 'default' || state === 'many') && uploadFromUrl && (
            <UploadFromUrl
              value={resolvedUploadUrl}
              placeholder={uploadUrlPlaceholder}
              onChange={updateUploadUrl}
              onUpload={onUrlUpload}
            />
          )}

          <FileUploadSupportText>{helpText}</FileUploadSupportText>

          {queueShown && (
            <UploadedFiles
              files={files}
              seenFileIds={seenFileIds.current}
              view={fileView}
              selectedFileIds={resolvedSelectedFileIds}
              onSelectedFileIdsChange={updateSelectedFileIds}
              onFileRemove={onFileRemove}
              onFileCancel={onFileCancel}
              onFileRetry={onFileRetry}
              onFilesDownload={onFilesDownload}
            />
          )}
        </div>
      </div>
    );
  },
);

FileUpload.displayName = 'FileUpload';

export {
  FileUpload,
  FileUploadArea,
  FileUploadItem,
  fileUploadAreaVariants,
  fileUploadItemVariants,
};
